import Image from "next/image"
import Link from "next/link"
import { cn } from "@/lib/utils"

export interface AgeCardProps {
  ageRange: string
  title: string
  description: string
  imageSrc: string
  linkText?: string
  linkHref?: string
  className?: string
}

export function AgeCard({
  ageRange,
  title,
  description,
  imageSrc,
  linkText,
  linkHref,
  className,
}: AgeCardProps) {
  const classes = cn(
    "pricing-card group rounded-[16px] overflow-hidden bg-neutral-100 flex flex-col transition-[border-color] duration-300 no-underline",
    className
  )

  const children = (
    <>
      {/* Image with badge */}
      <div className="relative m-3 aspect-[4/3] overflow-hidden rounded-[12px]">
        <Image
          src={imageSrc}
          alt={title}
          fill
          sizes="(min-width: 768px) 33vw, 100vw"
          className="object-cover object-[70%_center] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105 md:object-center"
        />
        <span className="absolute top-3 left-3 rounded-full border border-white/30 bg-black/[0.13] px-4 py-1.5 text-sm font-medium text-white backdrop-blur-md">
          {ageRange}
        </span>
      </div>

      {/* Inner white card — title + description */}
      <div className="mx-3 flex-1 rounded-[12px] bg-white px-5 py-5">
        <h3 className="type-title-lg text-aubergine-900">{title}</h3>
        <p className="type-body-lg mt-2 text-neutral-600">{description}</p>
      </div>

      {/* Bottom link — outside inner card */}
      {linkText && (
        <div className="px-6 py-4">
          <span className="type-body-lg inline-flex items-center gap-2 text-neutral-900">
            {linkText}{" "}
            <span
              aria-hidden="true"
              className="inline-block transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1"
            >
              →
            </span>
          </span>
        </div>
      )}
    </>
  )

  if (linkHref) {
    return (
      <Link href={linkHref} className={classes}>
        {children}
      </Link>
    )
  }
  return <div className={classes}>{children}</div>
}
